import React from 'react'
import { Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkMath from 'remark-math'
import rehypeKatex from 'rehype-katex'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism'

// Shared markdown components configuration
export const markdownComponents: Components = {
  // Paragraph with improved spacing
  // Note: 'node' prop added in react-markdown v9+ (contains AST node), must be destructured to avoid [object Object]
  p: ({ children, ...props }) => (
    <p className="leading-relaxed mb-4" style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }} {...props}>
      {children}
    </p>
  ),

  // Headers with proper spacing
  h1: ({ children, ...props }) => (
    <h1 className="text-2xl font-bold mb-4 mt-6" {...props}>
      {children}
    </h1>
  ),
  h2: ({ children, ...props }) => (
    <h2 className="text-xl font-semibold mb-3 mt-5" {...props}>
      {children}
    </h2>
  ),
  h3: ({ children, ...props }) => (
    <h3 className="text-lg font-semibold mb-2 mt-4" {...props}>
      {children}
    </h3>
  ),
  h4: ({ children, ...props }) => (
    <h4 className="text-base font-semibold mb-2 mt-3" {...props}>
      {children}
    </h4>
  ),

  // Lists with better spacing
  ul: ({ children, ...props }) => (
    <ul className="list-disc list-outside mb-4 space-y-1 ml-8" {...props}>
      {children}
    </ul>
  ),
  ol: ({ children, ...props }) => (
    <ol className="list-decimal list-outside mb-4 space-y-1 ml-8" {...props}>
      {children}
    </ol>
  ),
  li: ({ children, ...props }) => (
    <li className="leading-relaxed" {...props}>
      {children}
    </li>
  ),

  // Code blocks with syntax highlighting
  code: ({ className, children, ...props }) => {
    const match = /language-(\w+)/.exec(className || '')
    const isInline = !match

    // CRITICAL: Exclude language-math from SyntaxHighlighter
    // remark-math creates language-math nodes that must be handled by rehype-katex, not SyntaxHighlighter
    // This was causing [object Object] to appear where math should render
    if (match && (match[1] === 'math' || match[1] === 'math-inline' || match[1] === 'math-display')) {
      // Return plain code element so rehype-katex can process it
      return (
        <code className={className} {...props}>
          {children}
        </code>
      )
    }

    if (isInline) {
      return (
        <code
          className="px-1.5 py-0.5 bg-gray-100 dark:bg-dark-accent text-sm rounded font-mono"
          {...props}
        >
          {children}
        </code>
      )
    }

    // Skip Prism syntax highlighting for large code blocks (>10KB) to avoid
    // blocking the main thread for minutes on large files
    const codeString = String(children).replace(/\n$/, '')
    if (codeString.length > 10_000) {
      return (
        <pre className="rounded-md my-4 text-sm bg-gray-900 text-gray-100 p-4 overflow-x-auto">
          <code className="font-mono">{codeString}</code>
        </pre>
      )
    }

    return (
      <SyntaxHighlighter
        style={vscDarkPlus}
        language={match[1]}
        PreTag="div"
        className="rounded-md my-4 text-sm"
      >
        {codeString}
      </SyntaxHighlighter>
    )
  },

  // Pre blocks for code without language
  // Note: Math display blocks also use <pre>, so we need conditional styling
  // SyntaxHighlighter wraps code blocks with language - those get minimal styling
  // Plain code blocks (no language) need full styling to be visible
  // Math blocks need transparent background and no scroll/overflow
  pre: ({ children, ...props }) => {
    const childArray = React.Children.toArray(children)

    // Check if this pre contains math content (KaTeX equations)
    // Math blocks should have transparent background and no overflow/scrollbar
    const hasMathChild = childArray.some(
      (child) =>
        child &&
        typeof child === 'object' &&
        'props' in child &&
        (child.props?.className?.includes('language-math') ||
          child.props?.className?.includes('math-display') ||
          child.props?.className?.includes('math-inline'))
    )

    // Check if this pre contains a code block
    const hasCodeChild = childArray.some(
      (child) => child && typeof child === 'object' && 'type' in child && child.type === 'code'
    )

    // Math blocks: transparent background, no overflow (let equation size naturally)
    if (hasMathChild) {
      return (
        <pre className="bg-transparent p-0 my-2 overflow-visible border-none" {...props}>
          {children}
        </pre>
      )
    }

    return (
      <pre
        className={hasCodeChild
          ? "bg-gray-100 dark:bg-dark-accent text-gray-900 dark:text-dark-text p-4 rounded-md overflow-x-auto my-4 border border-gray-200 dark:border-dark-border font-mono"  // Light styling for plain code/ASCII diagrams (no language specified)
          : "p-0 rounded-md overflow-x-auto my-0.5"  // Minimal for SyntaxHighlighter-wrapped code (has language, uses its own dark theme)
        }
        {...props}
      >
        {children}
      </pre>
    )
  },

  // Blockquotes
  blockquote: ({ children, ...props }) => (
    <blockquote
      className="border-l-4 border-gray-300 dark:border-gray-600 pl-4 my-4 italic text-gray-700 dark:text-gray-300"
      {...props}
    >
      {children}
    </blockquote>
  ),

  // Tables with styling
  table: ({ children, ...props }) => (
    <div className="overflow-x-auto my-4">
      <table
        className="min-w-full border-collapse border border-gray-300 dark:border-gray-600"
        {...props}
      >
        {children}
      </table>
    </div>
  ),
  thead: ({ children, ...props }) => (
    <thead className="bg-gray-50 dark:bg-dark-muted" {...props}>
      {children}
    </thead>
  ),
  tbody: ({ children, ...props }) => (
    <tbody className="bg-white dark:bg-dark-card" {...props}>
      {children}
    </tbody>
  ),
  th: ({ children, ...props }) => (
    <th
      className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left font-semibold text-gray-900 dark:text-dark-text text-sm"
      {...props}
    >
      {children}
    </th>
  ),
  td: ({ children, ...props }) => (
    <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-gray-700 dark:text-gray-300 text-sm" {...props}>
      {children}
    </td>
  ),
  tr: ({ children, ...props }) => (
    <tr className="hover:bg-gray-50 dark:hover:bg-dark-muted" {...props}>
      {children}
    </tr>
  ),

  // Horizontal rules
  hr: ({ ...props }) => <hr className="my-6 border-gray-300 dark:border-gray-600" {...props} />,

  // Links
  a: ({ children, href, ...props }) => (
    <a
      href={href}
      className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline"
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    >
      {children}
    </a>
  ),

  // Strong/Bold
  strong: ({ children, ...props }) => (
    <strong className="font-semibold" {...props}>
      {children}
    </strong>
  ),

  // Emphasis/Italic
  em: ({ children, ...props }) => (
    <em className="italic" {...props}>
      {children}
    </em>
  ),
}

// Shared remark plugins with configuration
export const remarkPlugins = [
  remarkGfm,
  [
    remarkMath,
    {
      singleDollarTextMath: true, // Enable single $ for inline math. Required because preprocessor converts \(...\) to $...$. Code blocks are protected by preprocessor.
    },
  ],
]

// Shared rehype plugins with configuration
export const rehypePlugins = [
  [
    rehypeKatex,
    {
      strict: false, // Don't throw on errors
      throwOnError: false, // Render error messages inline instead of breaking
      errorColor: '#cc0000', // Red color for error messages
      trust: false, // Security: don't trust arbitrary LaTeX commands
      // NOTE: Removed 'output' option - react-markdown v10+ needs rehype-katex to use default output format
      // The 'output: html' option was causing [object Object] rendering issues

      // PR 9 (gap closure): pipe KaTeX errors into the
      // response-formatting observability channel. KaTeX's
      // errorCallback fires once per failed expression; we log
      // [KATEX_ERROR] so dashboards can track surviving failure
      // rate after the server-side normalizer (PR 4) lands. Inline
      // import keeps the observability module out of the bundle's
      // critical path for renders with no math errors.
      errorCallback: (msg: string) => {
        void import('@/features/chatV2/observability').then(({ logKatexError }) => {
          // KaTeX errors are formatted as "KaTeX parse error: ... at position N: ...".
          // The expression itself isn't passed to the callback — KaTeX strips it
          // before invocation. We log the error message; expression_len=0 is the
          // sentinel that this path doesn't have it.
          logKatexError({ expression: '', errorMessage: msg })
        })
      },
    },
  ],
]

// Export a function to merge custom components with defaults
export const mergeMarkdownComponents = (
  customComponents: Partial<Components> = {}
): Components => {
  return {
    ...markdownComponents,
    ...customComponents,
  }
}
